import React from 'react';
import { Image, StyleSheet, useWindowDimensions, } from 'react-native';
import banner from '../assets/images/AbbisWayLogo.png';
import wordmark from '../assets/images/AbbisWayWordmark.png';
import { WORDMARK_ASPECT_RATIO } from './AbbisWayWordmark';
import { LOGO_HEIGHT, LOGO_WIDTH } from './consts';
import { isNarrowWidth } from '../utils/useIsNarrowWindow';
/** Side margin kept around the logo on narrow windows. */
const NARROW_SIDE_MARGIN = 24;
/** Widest the wordmark gets on a narrow window. */
const NARROW_WORDMARK_MAX_WIDTH = 320;
/** Space above the wordmark on a narrow window. */
const NARROW_TOP_MARGIN = 16;
/**
 * Pure sizing, exported for tests.
 *
 * `availableWidth` is the width of the logo's container (the window, or the
 * pane beside the side panel). The boxed banner is used only when that is at
 * least the shared narrow breakpoint (600) and `variant` is 'auto'; otherwise
 * the mobile wordmark. Either way the logo is sized to fit inside
 * `availableWidth` minus side margins, so it never overflows or clips.
 */
export const fittedLogo = (availableWidth, variant = 'auto') => {
    const room = Math.max(0, availableWidth - 2 * NARROW_SIDE_MARGIN);
    if (variant === 'auto' && !isNarrowWidth(availableWidth)) {
        const width = Math.min(LOGO_WIDTH, room);
        return {
            narrow: false,
            style: {
                width,
                height: Math.round((width * LOGO_HEIGHT) / LOGO_WIDTH),
            },
        };
    }
    const width = Math.min(NARROW_WORDMARK_MAX_WIDTH, room);
    return {
        narrow: true,
        style: {
            width,
            height: Math.ceil(width / WORDMARK_ASPECT_RATIO),
            marginTop: NARROW_TOP_MARGIN,
        },
    };
};
/**
 * Logo for the web sign-in, sign-up and email-confirmation pages.
 *
 * Wide containers show the boxed AbbisWayLogo.png banner (at most
 * LOGO_WIDTH × LOGO_HEIGHT); narrow ones, or `variant="wordmark"` (stacked
 * layouts), show the mobile app's wordmark. Always scaled to fit
 * `availableWidth` (defaults to the window width) with the aspect ratio kept.
 */
export const FittedLogo = ({ style, availableWidth, variant = 'auto', }) => {
    const { width: windowWidth } = useWindowDimensions();
    const { narrow, style: sizeStyle } = fittedLogo(availableWidth ?? windowWidth, variant);
    return (<Image source={narrow ? wordmark : banner} style={[style, sizeStyle, styles.centered]} resizeMode="contain" accessibilityLabel="Abbi's Way"/>);
};
const styles = StyleSheet.create({
    centered: {
        alignSelf: 'center',
        maxWidth: '100%',
    },
});
